import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import DocumentEditor from './DocumentEditor';
import Layout from './Layout';
import Profile from './Profile';
import Settings from './Settings';
import ChangePassword from './ChangePassword';
import Schedule from './Schedule';
import TableSign from './TableSign';
import UnitSort from './UnitSort';
import Messages from './Messages';
// 管理后台
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminMessages from './admin/AdminMessages';
import Users from './admin/Users';
import Roles from './admin/Roles';
import Logs from './admin/Logs';
import Stats from './admin/Stats';
import AdminSettings from './admin/Settings';
import History from './admin/History';
import Announcements from './admin/Announcements';
import './App.css';

// 受保护的路由组件
const ProtectedRoute = ({ children }) => {
  const { user, loading, isGuest, guestLogin } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }
  
  // 如果没有登录且不是游客，自动设置为游客模式
  if (!user && !isGuest) {
    guestLogin();
  }
  
  return <Layout>{children}</Layout>;
};

// 公共路由组件（登录、注册、忘记密码页面）
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }
  
  // 已登录用户访问登录页时跳转到首页
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// 主应用组件
const AppContent = () => {
  return (
    <Routes>
      {/* 公共路由 */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      />
      
      {/* 受保护的路由 */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <DocumentEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/schedule" 
        element={
          <ProtectedRoute>
            <Schedule />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/table-sign" 
        element={
          <ProtectedRoute>
            <TableSign />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/unit-sort" 
        element={
          <ProtectedRoute>
            <UnitSort />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        } 
      />
      
      {/* 管理后台路由 */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="roles" element={<Roles />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="logs/login" element={<Logs type="login" />} />
        <Route path="logs/operation" element={<Logs type="operation" />} />
        <Route path="stats" element={<Stats />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="history" element={<History />} />
        <Route path="announcements" element={<Announcements />} />
      </Route>
      
      {/* 默认重定向 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
